import { Injectable, inject, computed } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TranslationService } from 'src/app/services/translation.service';
import { RegisterUserService, RegisterUserFormService } from '../../../../services';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { FormStepperService } from '../../../../components/register-user-form-mobile/services';
import { TripType } from '../../../../interfaces/register-user.response';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';
import { EMT_ORDER } from '../config/constants';

@Injectable({
  providedIn: 'root'
})
export class TripTypesFieldService {
  // Injected services
  private _translationService = inject(TranslationService);
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _formStepperService = inject(FormStepperService);
  private _breakpointsService = inject(BreakpointsService);

  // Computed properties from services (maintaining exact same logic)
  private _selectedLanguage = this._translationService.selectedLanguage;
  private _tripTypeList = this._registerUserService.registerUser().travelerType;
  private _currentTripTypeId = this._registerUserFormService.tripTypeId;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  private _isMobile = this._breakpointsService.isMobile;
  private _isNextButtonClicked = this._formStepperService.isNextButtonClicked;

  /**
   * Gets the ordered trip types list with EMT-specific hardcoded ordering
   * Maintains the exact same logic as the original component
   * @returns Array of trip types ordered according to EMT requirements
   */
  getOrderedTripTypes(): TripType[] {
    return this._tripTypeList
      .map(type => ({
        id: type.id,
        value: type.id,
        label: type.name[this._selectedLanguage()],
        iconName: type.icon,
      }))
      .sort((a, b) => {
        const orderA = EMT_ORDER[Number(a.id)] ?? 999;
        const orderB = EMT_ORDER[Number(b.id)] ?? 999;
        return orderA - orderB;
      });
  }

  /**
   * Handles the chip set trip type change event
   * Maintains the exact same logic as the original component
   * @param selectedTripType - The selected trip type object
   * @param control - The form control to update
   */
  handleTripTypeSelection(selectedTripType: TripType, control: AbstractControl | null): void {
    // Exact same logic as original component - passing the complete object
    this._registerUserFormService.setTripType(selectedTripType as any);
    
    control?.setValue(selectedTripType);
    control?.markAsTouched();
    control?.markAsDirty();
    
    if (this._isMobile()) {
      closeAllCiSnackbar();
    }
  }

  /**
   * Checks if a specific trip type is currently selected
   * Maintains the exact same logic as the original component
   * @param id - Trip type ID to check
   * @returns True if the trip type is selected
   */
  isSelectedTripType(id: number): boolean {
    return this._currentTripTypeId() 
      ? this._currentTripTypeId() === id
      : false;
  }

  /**
   * Validates if the field has errors and should display them
   * Maintains the exact same validation logic as the original component
   * @param control - The form control to validate
   * @returns True if field is invalid and should show errors
   */
  validateField(control: AbstractControl | null): boolean {
    if (!control?.hasError('required')) return false;
    
    // En mobile: solo mostrar error si el control específico ha sido tocado
    // Esto evita que errores de slides anteriores afecten a este campo
    if (this._isMobile()) {
      return (control.touched || control.dirty) && this._isNextButtonClicked();
    }
    
    // En desktop: comportamiento normal
    return control.touched || control.dirty;
  }

  /**
   * Marks form fields as touched for desktop validation
   * Maintains the exact same logic as the original component
   * @param control - The form control to mark as touched
   * @param cdr - Change detector reference for manual detection
   */
  markFieldsAsTouchedForDesktop(control: AbstractControl | null, cdr: any): void {
    if (this._markAllFormFieldsAsTouched()) {
      control?.markAsTouched();
      control?.markAsDirty();

      // Trigger change detection manually for OnPush (exact same as original)
      cdr.detectChanges();
    }
  }
}
